import React from 'react';
import ReactDOM from 'react-dom';

interface KeyboardShortcutsHelpProps {
  isOpen: boolean;
  onClose: () => void;
}

interface ShortcutItem {
  key: string;
  description: string;
}

interface ShortcutCategory {
  name: string;
  shortcuts: ShortcutItem[];
}

const shortcutCategories: ShortcutCategory[] = [
  {
    name: 'Canvas',
    shortcuts: [
      { key: 'S', description: 'Toggle selection mode' },
      { key: 'Ctrl+L', description: 'Auto-layout' },
      { key: 'Ctrl+G', description: 'Toggle grid' },
    ],
  },
  {
    name: 'View',
    shortcuts: [
      { key: 'Ctrl+0', description: 'Fit view' },
    ],
  },
  {
    name: 'History',
    shortcuts: [
      { key: 'Ctrl+Z', description: 'Undo' },
      { key: 'Ctrl+Y', description: 'Redo' },
    ],
  },
  {
    name: 'General',
    shortcuts: [
      { key: '?', description: 'Show shortcuts' },
      { key: 'Esc', description: 'Close dialogs' },
      { key: 'Delete', description: 'Delete selected' },
    ],
  },
];

const KeyboardShortcutsHelp: React.FC<KeyboardShortcutsHelpProps> = ({ isOpen, onClose }) => {
  const titleId = 'keyboard-shortcuts-title';

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="bg-white dark:bg-cap-dark rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-auto relative"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6">
          <h2 id={titleId} className="text-xl font-semibold mb-4">
            Keyboard Shortcuts
          </h2>

          <div className="space-y-6">
            {shortcutCategories.map((category) => (
              <div key={category.name}>
                <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                  {category.name}
                </h3>
                <div className="space-y-2">
                  {category.shortcuts.map((shortcut) => (
                    <div
                      key={shortcut.key}
                      className="flex items-center justify-between py-1"
                    >
                      <span className="text-gray-700 dark:text-gray-300">{shortcut.description}</span>
                      <kbd className="px-2 py-1 bg-gray-100 border border-gray-300 dark:border-gray-600 rounded dark:bg-gray-800 dark:text-warm-white text-sm font-mono">
                        {shortcut.key}
                      </kbd>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        <button
          className="absolute top-3 right-3 text-gray-400 hover:text-gray-700 dark:text-gray-300 text-2xl font-bold"
          onClick={onClose}
          aria-label="Close"
        >
          &times;
        </button>
      </div>
    </div>,
    document.body
  );
};

export default KeyboardShortcutsHelp;
